import { cn } from "@/lib/utils";

interface ProjectPreviewProps {
    industry: string;
    accent: string;
}

interface StatCardProps {
    title: string;
    value: string;
    accent: string;
}

function BrowserFrame({
    accent,
    children,
}: {
    accent: string;
    children: React.ReactNode;
}) {
    return (

        <div className="relative h-44 overflow-hidden rounded-t-[1.25rem]">

            <div
                className="absolute inset-0"
                style={{
                    background: `linear-gradient(135deg, ${accent}22, ${accent}08)`,
                }}
            />

            <div className="absolute inset-0 bg-grid opacity-40" />

            <div
                className="
                    absolute
                    left-5
                    right-5
                    top-5
                    overflow-hidden
                    rounded-xl
                    border
                    border-border
                    bg-card
                    shadow-[0_24px_60px_-30px_rgba(15,23,42,0.45)]
                    transition-transform
                    duration-500
                    group-hover:scale-[1.02]
                "
            >

                <div className="flex items-center gap-2 border-b border-border bg-background/80 px-3 py-2">

                    <span
                        className="h-2 w-2 rounded-full"
                        style={{ background: accent }}
                    />

                    <span className="h-2 w-2 rounded-full bg-muted" />

                    <span className="h-2 w-2 rounded-full bg-muted" />

                    <div className="ml-3 h-2 flex-1 rounded-full bg-muted" />

                </div>

                <div className="p-4">

                    {children}

                </div>

            </div>

        </div>

    );
}

function StatCard({
    title,
    value,
    accent,
}: StatCardProps) {

    return (

        <div className="rounded-lg border border-border bg-background p-3">

            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">

                {title}

            </div>

            <div
                className="mt-1 text-lg font-bold"
                style={{ color: accent }}
            >
                {value}
            </div>

        </div>

    );

}

function KpiGrid({
    accent,
    items,
}: {
    accent: string;
    items: {
        title: string;
        value: string;
    }[];
}) {

    return (

        <div className="grid grid-cols-2 gap-3">

            {items.map((item) => (

                <StatCard
                    key={item.title}
                    title={item.title}
                    value={item.value}
                    accent={accent}
                />

            ))}

        </div>

    );

}

function ErpPreview({ accent }: { accent: string }) {

    return (

        <BrowserFrame accent={accent}>

            <div className="mb-3 h-3 w-32 rounded-full bg-muted" />

            <KpiGrid
                accent={accent}
                items={[
                    {
                        title: "Revenue",
                        value: "₹12.8M",
                    },
                    {
                        title: "Orders",
                        value: "1,284",
                    },
                    {
                        title: "Inventory",
                        value: "98%",
                    },
                    {
                        title: "Suppliers",
                        value: "164",
                    },
                ]}
            />

        </BrowserFrame>

    );

}

function HealthcarePreview({
    accent,
}: {
    accent: string;
}) {

    return (

        <BrowserFrame accent={accent}>

            <div className="mb-3 h-3 w-40 rounded-full bg-muted" />

            <KpiGrid
                accent={accent}
                items={[
                    {
                        title: "Patients",
                        value: "248",
                    },
                    {
                        title: "Doctors",
                        value: "46",
                    },
                    {
                        title: "Appointments",
                        value: "132",
                    },
                    {
                        title: "Emergency",
                        value: "9",
                    },
                ]}
            />

        </BrowserFrame>

    );

}

function CrmPreview({
    accent,
}: {
    accent: string;
}) {

    return (

        <BrowserFrame accent={accent}>

            <div className="mb-3 flex items-center justify-between">

                <div className="h-3 w-28 rounded-full bg-muted" />

                <div
                    className="rounded-full px-2 py-1 text-[10px] font-semibold text-white"
                    style={{ background: accent }}
                >
                    +18%
                </div>

            </div>

            <KpiGrid
                accent={accent}
                items={[
                    {
                        title: "Leads",
                        value: "328",
                    },
                    {
                        title: "Pipeline",
                        value: "₹4.8M",
                    },
                    {
                        title: "Won",
                        value: "68",
                    },
                    {
                        title: "Forecast",
                        value: "91%",
                    },
                ]}
            />

        </BrowserFrame>

    );
}

function RestaurantPreview({
    accent,
}: {
    accent: string;
}) {

    return (

        <BrowserFrame accent={accent}>

            <div className="mb-3 h-3 w-36 rounded-full bg-muted" />

            <KpiGrid
                accent={accent}
                items={[
                    {
                        title: "Orders",
                        value: "184",
                    },
                    {
                        title: "Kitchen",
                        value: "23",
                    },
                    {
                        title: "Tables",
                        value: "41",
                    },
                    {
                        title: "Delivery",
                        value: "52",
                    },
                ]}
            />

        </BrowserFrame>

    );
}

function EducationPreview({
    accent,
}: {
    accent: string;
}) {

    return (

        <BrowserFrame accent={accent}>

            <div className="mb-3 h-3 w-40 rounded-full bg-muted" />

            <KpiGrid
                accent={accent}
                items={[
                    {
                        title: "Students",
                        value: "2,184",
                    },
                    {
                        title: "Faculty",
                        value: "146",
                    },
                    {
                        title: "Attendance",
                        value: "96%",
                    },
                    {
                        title: "Fees",
                        value: "₹82L",
                    },
                ]}
            />

        </BrowserFrame>

    );

}

function GovernmentPreview({
    accent,
}: {
    accent: string;
}) {

    return (

        <BrowserFrame accent={accent}>

            <div className="mb-3 h-3 w-44 rounded-full bg-muted" />

            <KpiGrid
                accent={accent}
                items={[
                    {
                        title: "Requests",
                        value: "1,284",
                    },
                    {
                        title: "Departments",
                        value: "18",
                    },
                    {
                        title: "Approvals",
                        value: "92%",
                    },
                    {
                        title: "Reports",
                        value: "76",
                    },
                ]}
            />

        </BrowserFrame>

    );

}

function AssetPreview({
    accent,
}: {
    accent: string;
}) {

    return (
        <BrowserFrame accent={accent}>

            <div className="mb-3 h-3 w-40 rounded-full bg-muted" />

            <KpiGrid
                accent={accent}
                items={[
                    {
                        title: "Assets",
                        value: "18,420",
                    },
                    {
                        title: "Active",
                        value: "97%",
                    },
                    {
                        title: "PM Due",
                        value: "186",
                    },
                    {
                        title: "Tickets",
                        value: "24",
                    },
                ]}
            />

        </BrowserFrame>
    );
}

function GenericPreview({
    accent,
}: {
    accent: string;
}) {

    return (

        <BrowserFrame accent={accent}>

            <div className="mb-3 h-3 w-32 rounded-full bg-muted" />

            <KpiGrid
                accent={accent}
                items={[
                    {
                        title: "Users",
                        value: "2.4K",
                    },
                    {
                        title: "Growth",
                        value: "18%",
                    },
                    {
                        title: "Tasks",
                        value: "438",
                    },
                    {
                        title: "Health",
                        value: "99%",
                    },
                ]}
            />
        </BrowserFrame>
    );
}

export function ProjectPreview({
    industry,
    accent,
}: ProjectPreviewProps) {

    const key = industry.toLowerCase();

    if (
        key.includes("manufacturing") ||
        key.includes("erp")
    ) {
        return <ErpPreview accent={accent} />;
    }

    if (
        key.includes("hospital") ||
        key.includes("health")
    ) {
        return <HealthcarePreview accent={accent} />;
    }

    if (
        key.includes("restaurant") ||
        key.includes("food")
    ) {
        return <RestaurantPreview accent={accent} />;
    }

    if (
        key.includes("education") ||
        key.includes("school")
    ) {
        return <EducationPreview accent={accent} />;
    }

    if (
        key.includes("government") ||
        key.includes("public")
    ) {
        return <GovernmentPreview accent={accent} />;
    }

    if (
        key.includes("asset")
    ) {
        return <AssetPreview accent={accent} />;
    }

    if (
        key.includes("crm") ||
        key.includes("sales")
    ) {
        return <CrmPreview accent={accent} />;
    }

    return <GenericPreview accent={accent} />;

}